import React, { useEffect, useState } from 'react';
import { ChevronDown, Plus, Filter, Search, MoreHorizontal, Clock, CheckCircle, AlertCircle, ArrowRight, ClipboardList, X } from 'lucide-react';
import { ActionDetailPanel } from './ActionDetailPanel';
import { ActionPanel, ActionFormData } from './ActionPanel';
import { SkeletonRow } from './Skeleton';
import { EmptyState } from './EmptyState';
import { useToast } from './Toast';
interface ActionLogProps {
  selectedMonth: 'current' | 'next';
  initialStatusFilter?: string | null;
  onClearFilters?: () => void;
}
interface Action {
  id: string;
  site: string;
  type: string;
  status: 'Open' | 'In Progress' | 'Resolved';
  assignedTo: string;
  createdDate: string;
  lastUpdated: string;
}
const initialActionsData: Action[] = [{
  id: '1042',
  site: 'Woolworths HQ',
  type: 'Real-Time Exception',
  status: 'In Progress',
  assignedTo: 'Sarah van Wyk',
  createdDate: '2 Feb 2026',
  lastUpdated: '2 Feb 2026'
}, {
  id: '1041',
  site: 'Standard Bank Tower',
  type: 'Roster Optimisation',
  status: 'Open',
  assignedTo: 'Unassigned',
  createdDate: '1 Feb 2026',
  lastUpdated: '1 Feb 2026'
}, {
  id: '1040',
  site: 'Pick n Pay Constantia',
  type: 'Real-Time Exception',
  status: 'Resolved',
  assignedTo: 'James Nkosi',
  createdDate: '31 Jan 2026',
  lastUpdated: '1 Feb 2026'
}, {
  id: '1039',
  site: 'Nedbank Plaza',
  type: 'Roster Optimisation',
  status: 'In Progress',
  assignedTo: 'Sarah van Wyk',
  createdDate: '30 Jan 2026',
  lastUpdated: '31 Jan 2026'
}, {
  id: '1038',
  site: 'Checkers Claremont',
  type: 'Real-Time Exception',
  status: 'Resolved',
  assignedTo: 'Thabo Molefe',
  createdDate: '29 Jan 2026',
  lastUpdated: '30 Jan 2026'
}, {
  id: '1037',
  site: 'FNB Building',
  type: 'Roster Optimisation',
  status: 'Open',
  assignedTo: 'Unassigned',
  createdDate: '28 Jan 2026',
  lastUpdated: '28 Jan 2026'
}, {
  id: '1036',
  site: 'Spar Distribution Centre',
  type: 'Real-Time Exception',
  status: 'In Progress',
  assignedTo: 'Linda Khumalo',
  createdDate: '27 Jan 2026',
  lastUpdated: '28 Jan 2026'
}, {
  id: '1035',
  site: 'Dischem Warehouse',
  type: 'Roster Optimisation',
  status: 'Resolved',
  assignedTo: 'James Nkosi',
  createdDate: '26 Jan 2026',
  lastUpdated: '27 Jan 2026'
}];
export function ActionLog({
  selectedMonth,
  initialStatusFilter,
  onClearFilters
}: ActionLogProps) {
  const {
    showToast
  } = useToast();
  const [actions, setActions] = useState<Action[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>('All Types');
  const [statusFilter, setStatusFilter] = useState<string>(initialStatusFilter || 'All Statuses');
  const [viewingAction, setViewingAction] = useState<Action | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // Update status filter if initialStatusFilter changes
  useEffect(() => {
    if (initialStatusFilter) {
      setStatusFilter(initialStatusFilter);
    }
  }, [initialStatusFilter]);
  // Simulate initial data loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setActions(initialActionsData);
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);
  const filteredActions = actions.filter(action => {
    const typeMatch = typeFilter === 'All Types' || action.type === typeFilter;
    const statusMatch = statusFilter === 'All Statuses' || action.status === statusFilter;
    return typeMatch && statusMatch;
  });
  const handleClearFilters = () => {
    setTypeFilter('All Types');
    setStatusFilter('All Statuses');
    onClearFilters?.();
  };
  const isFiltered = typeFilter !== 'All Types' || statusFilter !== 'All Statuses';
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Open':
        return 'bg-sky-500 text-white';
      case 'In Progress':
        return 'bg-amber-500 text-white';
      case 'Resolved':
        return 'bg-emerald-500 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };
  const getTypeStyles = (type: string) => {
    if (type === 'Real-Time Exception') {
      return 'bg-[#ff697f]/10 text-[#d94a5f] border-[#ff697f]/15';
    }
    return 'bg-[#062133]/5 text-[#5a7280] border-[#062133]/10';
  };
  const handleCreateAction = (data: ActionFormData) => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    const newAction: Action = {
      id: String(Math.floor(Math.random() * 1000) + 1043),
      site: data.site,
      type: data.actionType,
      status: 'Open',
      assignedTo: data.assignTo,
      createdDate: dateStr,
      lastUpdated: dateStr
    };
    // Add new action to top of list
    setActions(prev => [newAction, ...prev]);
    // Close create panel
    setIsCreateOpen(false);
    // Show toast
    showToast('Action created successfully', 'success');
    // Open detail panel for the new action
    setViewingAction(newAction);
  };
  return <div className="flex flex-col space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-103">
      {/* Filter Bar */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 bg-white p-4 rounded-xl border border-[#e5e5e5] shadow-sm" data-id="element-104">
        <div className="flex items-center justify-between md:hidden" data-id="element-105">
          <div className="flex items-center gap-2 text-[#062133] font-medium" data-id="element-106">
            <Filter className="h-4 w-4" data-id="element-107" />
            <span data-id="element-108">Filters</span>
          </div>
          {isFiltered && <button onClick={handleClearFilters} className="text-xs font-medium text-[#ff697f]" data-id="element-109">
              Clear all
            </button>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1" data-id="element-110">
          {/* Action Type Filter */}
          <div className="relative" data-id="element-111">
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={`w-full appearance-none bg-white border text-[#062133] text-sm rounded-xl px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-[#ff697f] focus:border-transparent cursor-pointer min-h-[44px] transition-colors ${typeFilter !== 'All Types' ? 'border-[#ff697f] bg-[#ff697f]/5 font-medium' : 'border-[#e5e5e5] hover:border-[#5a7280]'}`} data-id="element-112">
              <option data-id="element-113">All Types</option>
              <option data-id="element-114">Roster Optimisation</option>
              <option data-id="element-115">Real-Time Exception</option>
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none ${typeFilter !== 'All Types' ? 'text-[#ff697f]' : 'text-[#5a7280]'}`} data-id="element-116" />
          </div>

          {/* Status Filter */}
          <div className="relative" data-id="element-117">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`w-full appearance-none bg-white border text-[#062133] text-sm rounded-xl px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-[#ff697f] focus:border-transparent cursor-pointer min-h-[44px] transition-colors ${statusFilter !== 'All Statuses' ? 'border-[#ff697f] bg-[#ff697f]/5 font-medium' : 'border-[#e5e5e5] hover:border-[#5a7280]'}`} data-id="element-118">
              <option data-id="element-119">All Statuses</option>
              <option data-id="element-120">Open</option>
              <option data-id="element-121">In Progress</option>
              <option data-id="element-122">Resolved</option>
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none ${statusFilter !== 'All Statuses' ? 'text-[#ff697f]' : 'text-[#5a7280]'}`} data-id="element-123" />
          </div>
        </div>

        <div className="flex items-center gap-3 md:ml-auto" data-id="element-124">
          {isFiltered && <button onClick={handleClearFilters} className="hidden md:flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-[#5a7280] hover:text-[#062133] hover:bg-[#fafaf9] rounded-xl transition-colors min-h-[44px]" data-id="element-125">
              <X className="h-4 w-4" data-id="element-126" />
              Clear filters
            </button>}

          <button onClick={() => setIsCreateOpen(true)} className="w-full md:w-auto flex items-center justify-center gap-2 bg-[#ff697f] text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-[#e65a6f] transition-colors shadow-sm min-h-[44px]" data-id="element-127">
            <Plus className="h-4 w-4" data-id="element-128" />
            Create Action
          </button>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-hidden rounded-xl border border-[#e5e5e5] bg-white shadow-sm" data-id="element-129">
        <table className="min-w-full divide-y divide-[#e5e5e5]" data-id="element-130">
          <thead className="bg-[#fafaf9]" data-id="element-131">
            <tr data-id="element-132">
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-133">
                Action ID
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-134">
                Site
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-135">
                Type
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-136">
                Status
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-137">
                Assigned To
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-138">
                Created
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-139">
                Last Updated
              </th>
              <th scope="col" className="relative px-6 py-4" data-id="element-140">
                <span className="sr-only" data-id="element-141">View</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#e5e5e5]" data-id="element-142">
            {isLoading ? <>
                <SkeletonRow cols={8} data-id="element-143" />
                <SkeletonRow cols={8} data-id="element-144" />
                <SkeletonRow cols={8} data-id="element-145" />
                <SkeletonRow cols={8} data-id="element-146" />
                <SkeletonRow cols={8} data-id="element-147" />
              </> : filteredActions.length > 0 ? filteredActions.map(action => <tr key={action.id} onClick={() => setViewingAction(action)} className="hover:bg-[#fafaf9] transition-colors cursor-pointer group" data-id="element-148">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#062133]" data-id="element-149">
                    #{action.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#062133]" data-id="element-150">
                    {action.site}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-151">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeStyles(action.type)}`} data-id="element-152">
                      {action.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-153">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getStatusBadge(action.status)}`} data-id="element-154">
                      {action.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#5a7280]" data-id="element-155">
                    {action.assignedTo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#5a7280]" data-id="element-156">
                    {action.createdDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#5a7280]" data-id="element-157">
                    {action.lastUpdated}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-158">
                    <ArrowRight className="h-4 w-4 text-[#e5e5e5] group-hover:text-[#ff697f] transition-colors" data-id="element-159" />
                  </td>
                </tr>) : <tr data-id="element-160">
                <td colSpan={8} className="p-0" data-id="element-161">
                  <div className="border-none shadow-none" data-id="element-162">
                    <EmptyState icon={ClipboardList} title="No actions found" description={actions.length === 0 ? 'Create your first action to start tracking issues' : 'Try adjusting your filters to see more results'} actionLabel={actions.length === 0 ? 'Create Action' : undefined} onAction={actions.length === 0 ? () => setIsCreateOpen(true) : undefined} data-id="element-163" />
                  </div>
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4" data-id="element-164">
        {isLoading ? <>
            {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl border border-[#e5e5e5] p-5 shadow-sm space-y-4 animate-pulse" data-id="element-165">
                <div className="flex justify-between" data-id="element-166">
                  <div className="h-4 bg-[#e5e5e5] rounded w-24" data-id="element-167" />
                  <div className="h-4 bg-[#e5e5e5] rounded w-16" data-id="element-168" />
                </div>
                <div className="h-4 bg-[#e5e5e5] rounded w-32" data-id="element-169" />
                <div className="grid grid-cols-2 gap-4" data-id="element-170">
                  <div className="h-4 bg-[#e5e5e5] rounded w-full" data-id="element-171" />
                  <div className="h-4 bg-[#e5e5e5] rounded w-full" data-id="element-172" />
                </div>
              </div>)}
          </> : filteredActions.length > 0 ? filteredActions.map(action => <div key={action.id} onClick={() => setViewingAction(action)} className="bg-white rounded-xl border border-[#e5e5e5] p-5 shadow-sm active:bg-[#fafaf9] transition-colors cursor-pointer" data-id="element-173">
              <div className="flex justify-between items-start mb-3" data-id="element-174">
                <div data-id="element-175">
                  <span className="text-xs font-medium text-[#5a7280]" data-id="element-176">
                    #{action.id}
                  </span>
                  <h3 className="text-base font-bold text-[#062133] mt-0.5" data-id="element-177">
                    {action.site}
                  </h3>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getStatusBadge(action.status)}`} data-id="element-178">
                  {action.status}
                </span>
              </div>

              <div className="mb-4" data-id="element-179">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeStyles(action.type)}`} data-id="element-180">
                  {action.type}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-y-3 text-sm border-t border-[#e5e5e5] pt-3" data-id="element-181">
                <div data-id="element-182">
                  <p className="text-xs text-[#5a7280] mb-0.5" data-id="element-183">Assigned To</p>
                  <p className="font-medium text-[#062133]" data-id="element-184">
                    {action.assignedTo}
                  </p>
                </div>
                <div className="text-right" data-id="element-185">
                  <p className="text-xs text-[#5a7280] mb-0.5" data-id="element-186">Last Updated</p>
                  <p className="font-medium text-[#062133]" data-id="element-187">
                    {action.lastUpdated}
                  </p>
                </div>
              </div>
            </div>) : <EmptyState icon={ClipboardList} title="No actions found" description={actions.length === 0 ? 'Create your first action to start tracking issues' : 'Try adjusting your filters to see more results'} actionLabel={actions.length === 0 ? 'Create Action' : undefined} onAction={actions.length === 0 ? () => setIsCreateOpen(true) : undefined} data-id="element-188" />}
      </div>

      {/* Load More */}
      {filteredActions.length > 0 && !isLoading && <div className="flex justify-center pt-4 pb-8" data-id="element-189">
          <button className="px-6 py-2.5 text-sm font-medium text-[#5a7280] bg-white border border-[#e5e5e5] rounded-xl hover:bg-[#fafaf9] hover:text-[#062133] transition-colors min-h-[44px]" data-id="element-190">
            Load More
          </button>
        </div>}

      {/* Panels */}
      <ActionDetailPanel isOpen={viewingAction !== null} siteName={viewingAction?.site || ''} onClose={() => setViewingAction(null)} data-id="element-191" />

      <ActionPanel isOpen={isCreateOpen} siteName="" mode="site-searchable" onClose={() => setIsCreateOpen(false)} onSubmit={handleCreateAction} data-id="element-192" />
    </div>;
}